/**
 * Marketing attribution — remembering which page earned an enquiry.
 *
 * ── The problem this exists to solve ──────────────────────────────────────────
 * The contact form used to read its attribution off the URL of /contact at the
 * moment of submit. For a Meta ad that is exactly right: the ad lands the click
 * on the form with its UTMs attached, so last touch and first touch are the
 * same event. It is the reason paid was measurable from day one.
 *
 * For search it answers nothing. An organic visitor lands on a guide or a city
 * page, reads it, and only then clicks through — and by that point the URL
 * carries no parameters at all, so `landingPath` read '/contact' on every
 * single row. `document.referrer` is not the escape hatch it looks like
 * either: it is NOT updated by client-side navigation, so after a `next/link`
 * hop it still says google.com. True, and useless — we already knew it was
 * Google. What we could never tell was WHICH of our own 63 pages did the work.
 *
 * So first touch is captured once, on the first public page of the visit, and
 * carried in sessionStorage until the form is submitted.
 *
 * ── Why sessionStorage and not a cookie ──────────────────────────────────────
 * This is one visit's worth of routing information, read by one page in the
 * same tab. A cookie would be sent on every request to the API for no reason,
 * would need a consent story it does not currently need (the cookie policy
 * promises no advertising cookies), and would outlive the visit it describes.
 * The cost is that a visitor who comes back tomorrow in a new tab reads as a
 * fresh first touch — correct for this purpose: the page that earned THIS
 * enquiry is the one they entered on this time.
 *
 * Every access is wrapped: Safari private mode throws on sessionStorage rather
 * than returning null, and attribution must never be the reason a form breaks.
 */

const KEY = 'bsn.firstTouch.v1';

export interface FirstTouch {
  /** The first public page of the visit, e.g. '/guides/ra-bill-format'. */
  path: string;
  /** Off-site referrer of that first page — a search engine, usually. */
  referrer: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  /** ISO timestamp of the capture. */
  at: string;
}

export interface Attribution {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  fbclid: string;
  gclid: string;
  referrer: string;
  landingPath: string;
  /** The page whose CTA was clicked, from `?from=` — see lib/contactHref.ts. */
  ctaFrom: string;
  firstTouch?: FirstTouch;
}

function readParams(): URLSearchParams {
  try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); }
}

/**
 * Record the first public page of this visit, if it has not been recorded yet.
 *
 * Idempotent and first-write-wins: the whole point is that the SECOND page
 * must not overwrite the first. Call it from every public page (see
 * components/AttributionCapture.tsx); calling it more often is free.
 */
export function captureFirstTouch(pathname: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(KEY)) return;
    const p = readParams();
    const touch: FirstTouch = {
      path: pathname || window.location.pathname,
      // Only an OFF-SITE referrer is worth storing. On a first page load our
      // own host can only appear if the visit actually started elsewhere on
      // the site and this capture is late (a hard reload mid-visit), in which
      // case naming ourselves as the source of our own lead is worse than
      // saying nothing.
      referrer: isOffSiteReferrer(document.referrer) ? document.referrer : '',
      utmSource: p.get('utm_source') || '',
      utmMedium: p.get('utm_medium') || '',
      utmCampaign: p.get('utm_campaign') || '',
      at: new Date().toISOString(),
    };
    window.sessionStorage.setItem(KEY, JSON.stringify(touch));
  } catch {
    /* Private mode, storage disabled, quota — measurement is never worth an
       exception on a marketing page. */
  }
}

/** True when `ref` is a real external referrer rather than our own site. */
function isOffSiteReferrer(ref: string): boolean {
  if (!ref) return false;
  try {
    return new URL(ref).hostname !== window.location.hostname;
  } catch {
    return false;
  }
}

export function readFirstTouch(): FirstTouch | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as Partial<FirstTouch>;
    return typeof v?.path === 'string' ? (v as FirstTouch) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Everything the contact form sends as `attribution`: last touch off the
 * current URL (unchanged behaviour — Meta leads must keep working exactly as
 * they do) plus the first touch recorded at the start of the visit.
 */
export function readAttribution(): Attribution | Record<string, never> {
  if (typeof window === 'undefined') return {};
  const p = readParams();
  return {
    utmSource: p.get('utm_source') || '',
    utmMedium: p.get('utm_medium') || '',
    utmCampaign: p.get('utm_campaign') || '',
    fbclid: p.get('fbclid') || '',
    gclid: p.get('gclid') || '',
    referrer: document.referrer || '',
    landingPath: window.location.pathname + window.location.search,
    ctaFrom: p.get('from') || '',
    firstTouch: readFirstTouch(),
  };
}
